import { useCallback, useEffect, useRef, useState } from "react";
import { TimeslotId, Timeslot } from "@core/features/timeslots";

type UseTimeSlotSelectionOptions = {
  allTimeSlots: Timeslot[];
  savedTimeSlots: TimeslotId[];
};

export function useTimeslotSelection({
  allTimeSlots,
  savedTimeSlots,
}: UseTimeSlotSelectionOptions) {
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<Timeslot[]>([]);

  const [_isTouchDragging, setIsTouchDragging] = useState(false);

  const [isSelecting, setIsSelecting] = useState(false);

  const [isDeselecting, setIsDeselecting] = useState(false);

  const [selectionStart, setSelectionStart] = useState<Timeslot | null>(null);

  const lastTouchRef = useRef<{
    dayIndex: number;
    hour: number;
    minute: number;
  } | null>(null);

  const hasDraggedRef = useRef(false);

  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, slot: Timeslot) => {
      e.preventDefault();
      const isSelected = selectedTimeSlots.some((s) => s.id === slot.id);

      setSelectionStart(slot);

      if (isSelected) {
        setIsDeselecting(true);
        setSelectedTimeSlots((prev) => prev.filter((s) => s.id !== slot.id));
      } else {
        setIsSelecting(true);
        setSelectedTimeSlots((prev) => [...prev, slot]);
      }
    },
    [selectedTimeSlots]
  );

  const handleMouseOver = useCallback(
    (_e: React.MouseEvent | React.TouchEvent, slot: Timeslot) => {
      if ((!isSelecting && !isDeselecting) || !selectionStart) return;
      if (slot.local.start.dayIndex !== selectionStart.local.start.dayIndex)
        return;

      const start =
        selectionStart.local.start.hour * 60 +
        selectionStart.local.start.minute;
      const current = slot.local.start.hour * 60 + slot.local.start.minute;
      const [min, max] = [Math.min(start, current), Math.max(start, current)];

      setSelectedTimeSlots((prev) => {
        const updated = [...prev];

        for (let h = 0; h < 24; h++) {
          for (const m of [0, 30]) {
            const t = h * 60 + m;
            if (t < min || t > max) continue;

            const match = allTimeSlots.find(
              (s) =>
                s.local.start.dayIndex === slot.local.start.dayIndex &&
                s.local.start.hour === h &&
                s.local.start.minute === m
            );

            if (!match) continue;

            if (isSelecting && !updated.some((s) => s.id === match.id)) {
              updated.push(match);
            }

            if (isDeselecting) {
              const i = updated.findIndex((s) => s.id === match.id);
              if (i !== -1) updated.splice(i, 1);
            }
          }
        }

        return updated;
      });
    },
    [isSelecting, isDeselecting, selectionStart, allTimeSlots]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent, slot: Timeslot) => {
      const touch = e.touches[0];
      touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
      setSelectionStart(slot);
      setIsTouchDragging(false);
      hasDraggedRef.current = false;

      const isSelected = selectedTimeSlots.some((s) => s.id === slot.id);

      if (isSelected) {
        setIsDeselecting(true);
        setSelectedTimeSlots((prev) => prev.filter((s) => s.id !== slot.id));
      } else {
        setIsSelecting(true);
        setSelectedTimeSlots((prev) => [...prev, slot]);
      }

      lastTouchRef.current = {
        dayIndex: slot.local.start.dayIndex,
        hour: slot.local.start.hour,
        minute: slot.local.start.minute,
      };
    },
    [selectedTimeSlots]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      const element = document.elementFromPoint(touch.clientX, touch.clientY);
      if (!element || !element.classList.contains("time-slot")) return;

      const idAttr = element.getAttribute("data-slot-id");
      if (!idAttr) return;

      const slot = allTimeSlots.find((s) => s.id.toString() === idAttr);
      if (
        !slot ||
        slot.local.start.dayIndex !== selectionStart?.local.start.dayIndex
      )
        return;

      if (
        !lastTouchRef.current ||
        lastTouchRef.current.dayIndex !== slot.local.start.dayIndex ||
        lastTouchRef.current.hour !== slot.local.start.hour ||
        lastTouchRef.current.minute !== slot.local.start.minute
      ) {
        lastTouchRef.current = {
          dayIndex: slot.local.start.dayIndex,
          hour: slot.local.start.hour,
          minute: slot.local.start.minute,
        };

        hasDraggedRef.current = true;
        setIsTouchDragging(true);
        handleMouseOver(e, slot);
      }
    },
    [allTimeSlots, handleMouseOver, selectionStart]
  );

  const clearSelection = useCallback(() => {
    setSelectedTimeSlots([]);
  }, []);

  const reset = useCallback(() => {
    setIsSelecting(false);
    setIsDeselecting(false);
    setIsTouchDragging(false);
    setSelectionStart(null);
    lastTouchRef.current = null;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.changedTouches[0];
      const element = document.elementFromPoint(touch.clientX, touch.clientY);

      const start = touchStartPosRef.current;
      touchStartPosRef.current = null;

      if (!element || !element.classList.contains("time-slot")) {
        reset();
        return;
      }

      const idAttr = element.getAttribute("data-slot-id");
      if (!idAttr) {
        reset();
        return;
      }

      const slot = allTimeSlots.find((s) => s.id.toString() === idAttr);
      if (!slot) {
        reset();
        return;
      }

      const dx = touch.clientX - (start?.x ?? 0);
      const dy = touch.clientY - (start?.y ?? 0);
      const moved = Math.sqrt(dx * dx + dy * dy);

      const isTap = moved < 10; // px threshold

      if (isTap) {
        const isSelected = selectedTimeSlots.some((s) => s.id === slot.id);
        if (isSelected) {
          setSelectedTimeSlots((prev) => prev.filter((s) => s.id !== slot.id));
        } else {
          setSelectedTimeSlots((prev) => [...prev, slot]);
        }
      }

      reset();
    },
    [allTimeSlots, selectedTimeSlots, reset]
  );

  useEffect(() => {
    const cancel = () => reset();
    document.addEventListener("mouseup", cancel);
    return () => {
      document.removeEventListener("mouseup", cancel);
    };
  }, [reset]);

  const hasInitializedFromSaved = useRef(false);

  useEffect(() => {
    if (
      !hasInitializedFromSaved.current &&
      savedTimeSlots.length > 0 &&
      allTimeSlots.length > 0
    ) {
      const initial = savedTimeSlots
        .map((id) => allTimeSlots.find((s) => s.id === id))
        .filter((s): s is Timeslot => s !== undefined);

      if (initial.length > 0) {
        setSelectedTimeSlots(initial);
        hasInitializedFromSaved.current = true;
      }
    }
  }, [savedTimeSlots, allTimeSlots]);

  useEffect(() => {
    setSelectedTimeSlots((prevSelected) => {
      const updated = prevSelected
        .map((slot) => allTimeSlots.find((s) => s.id === slot.id))
        .filter((s): s is Timeslot => s !== undefined);

      const sameLength = updated.length === prevSelected.length;
      const sameIds =
        sameLength && updated.every((s, i) => s.id === prevSelected[i]?.id);

      return sameIds ? prevSelected : updated;
    });
  }, [allTimeSlots, savedTimeSlots]);

  return {
    selectedTimeSlots,
    setSelectedTimeSlots,
    handleMouseDown,
    handleMouseOver,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    clearSelection,
    isSelecting,
    isDeselecting,
    selectionStart,
  };
}

export default useTimeslotSelection;

